import type { AppLocale } from '../job/summer-job-job-type';
import { normalizeJobTypeForLocale, normalizeRemotenessForLocale } from '../data/processor-output-fields';
import {
  resolveEducationLevelFromUrl,
  resolveLanguageNameFromUrl,
  resolvePeopleManagementFromUrl,
  resolveRoleLevelFromUrl,
} from '../data/validate-job-fields';

function capitalizeWordsLoose(s: string): string {
  return s
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w.length ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
}

/**
 * Maps a facet value from the URL back to the form expected by Algolia.
 * Query params are stored lowercase (see `generateUrlFromIndexUiState`); this reverses that
 * using known enums and sensible word-casing fallbacks.
 */
export function normalizeFilterValue(attribute: string, value: string, locale: AppLocale = 'en'): string {
  const attrLower = attribute.toLowerCase();
  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed;

  // Company IDs are usually opaque (preserve mixed-case / symbols). Path and query slugs are
  // lowercased for stable URLs (`/company/poolside`); map those back to facet casing.
  if (attrLower === 'companyid') {
    if (trimmed !== trimmed.toLowerCase()) {
      return trimmed;
    }
    return capitalizeWordsLoose(trimmed.replace(/[-_]+/g, ' '));
  }

  if (attrLower === 'remoteness') {
    return normalizeRemotenessForLocale(trimmed, locale) ?? capitalizeWordsLoose(trimmed);
  }

  if (attrLower === 'jobtype') {
    const direct = normalizeJobTypeForLocale(trimmed, locale);
    if (direct) return direct;
    const hyphenated = normalizeJobTypeForLocale(trimmed.replace(/\s+/g, '-'), locale);
    if (hyphenated) return hyphenated;
    const spaced = normalizeJobTypeForLocale(trimmed.replace(/-/g, ' '), locale);
    return spaced ?? capitalizeWordsLoose(trimmed);
  }

  if (attrLower === 'normalisedrolelevel') {
    return resolveRoleLevelFromUrl(trimmed, locale);
  }

  if (attrLower === 'educationlevel') {
    return resolveEducationLevelFromUrl(trimmed, locale);
  }

  if (attrLower === 'peoplemanagementtier') {
    return resolvePeopleManagementFromUrl(trimmed, locale);
  }

  if (attrLower === 'requiredlanguages' || attrLower === 'optionallanguages') {
    return resolveLanguageNameFromUrl(trimmed);
  }

  if (
    attrLower === 'normalisedtitle' ||
    attrLower === 'field' ||
    attrLower === 'companyindustry' ||
    attrLower.startsWith('companyindustry_')
  ) {
    return capitalizeWordsLoose(trimmed);
  }

  return capitalizeWordsLoose(trimmed);
}
